import React, { useState } from "react";
import { fmtPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Target, ShieldAlert, Cpu, Lock,
  Copy, Crown, Sparkles, Activity, CheckCircle2, XCircle,
} from "lucide-react";

const catLabel = { metals: "GOLD", forex: "FOREX", crypto: "CRYPTO" };

export const SignalCard = ({ signal, onUpgrade }) => {
  const [open, setOpen] = useState(false);
  const isBuy = signal.signal === "BUY";
  const locked = signal.locked;
  const dec = signal.decimals ?? 4;
  const won = signal.status === "won";
  const lost = signal.status === "lost";

  const copy = () => {
    const txt = `${signal.pair} ${signal.signal}\nEntry: ${signal.entry}\nSL: ${signal.stop_loss}\nTP1: ${signal.take_profit_1} TP2: ${signal.take_profit_2} TP3: ${signal.take_profit_3}\nConfidence: ${signal.confidence}%`;
    navigator.clipboard.writeText(txt);
    toast.success("Signal copied to clipboard");
  };

  return (
    <>
      <div
        data-testid={`signal-card-${signal.pair.replace("/", "").toLowerCase()}`}
        className={`qs-card p-5 flex flex-col gap-4 transition-transform duration-200 hover:-translate-y-1 ${
          signal.status === "active" ? (isBuy ? "qs-glow-buy" : "qs-glow-sell") : ""
        }`}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-xl font-bold tracking-tight">{signal.pair}</span>
              <Badge variant="outline" className="border-slate-600 text-[10px] text-slate-400 font-mono-ticker">
                {catLabel[signal.category] || signal.category?.toUpperCase()}
              </Badge>
              {signal.premium && <Crown size={14} className="text-amber-400" />}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{signal.name} · {signal.timeframe}</p>
          </div>
          <div
            data-testid={`signal-${isBuy ? "buy" : "sell"}-badge`}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${
              isBuy ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
            }`}
          >
            {isBuy ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
            {signal.signal}
          </div>
        </div>

        {/* confidence */}
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-slate-400 flex items-center gap-1"><Cpu size={12} className="text-cyan-400" /> AI Confidence</span>
            <span className="font-mono-ticker font-semibold text-slate-200">{signal.confidence}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400" style={{ width: `${signal.confidence}%` }} />
          </div>
        </div>

        {locked ? (
          <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-5 text-center">
            <Lock className="mx-auto text-amber-400 mb-2" size={22} />
            <p className="text-sm text-slate-300 font-medium">Premium Gold / Crypto signal</p>
            <p className="text-xs text-slate-500 mt-1">Entry, SL, TP levels & AI reasoning locked.</p>
            <Button
              data-testid={`signal-unlock-${signal.id}`}
              onClick={onUpgrade}
              className="mt-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full h-8 px-4 text-xs"
            >
              Upgrade to unlock
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Entry" value={fmtPrice(signal.entry, dec)} color="text-slate-100" icon={<Activity size={12} />} />
              <Stat label="Stop Loss" value={fmtPrice(signal.stop_loss, dec)} color="text-red-400" icon={<ShieldAlert size={12} />} />
              <Stat label="Take Profit" value={fmtPrice(signal.take_profit_1, dec)} color="text-emerald-400" icon={<Target size={12} />} />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>R:R <span className="text-cyan-400 font-semibold font-mono-ticker">{signal.risk_reward}</span></span>
              <div className="flex gap-3 font-mono-ticker">
                <span>TP2 {fmtPrice(signal.take_profit_2, dec)}</span>
                <span>TP3 {fmtPrice(signal.take_profit_3, dec)}</span>
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-between pt-1 border-t border-slate-800/80 mt-auto">
          <div>
            {signal.status === "active" && <span className="text-xs text-emerald-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Active</span>}
            {won && <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 size={13} /> +{signal.outcome_pips} pips</span>}
            {lost && <span className="text-xs text-red-400 flex items-center gap-1"><XCircle size={13} /> {signal.outcome_pips} pips</span>}
          </div>
          <div className="flex gap-1">
            {!locked && (
              <Button data-testid={`signal-copy-${signal.id}`} variant="ghost" size="sm" onClick={copy} className="h-8 text-slate-400 hover:text-white px-2">
                <Copy size={14} />
              </Button>
            )}
            <Button
              data-testid={`signal-analysis-${signal.id}`}
              variant="ghost"
              size="sm"
              onClick={() => (locked ? onUpgrade() : setOpen(true))}
              className="h-8 text-cyan-400 hover:text-cyan-300 px-2 text-xs font-semibold"
            >
              <Sparkles size={14} className="mr-1" /> AI Analysis
            </Button>
          </div>
        </div>
      </div>

      {!locked && signal.reasoning && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="qs-card border-slate-700 text-slate-100 sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ai-analysis-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <Cpu className="text-cyan-400" /> {signal.pair}
                <span className={`text-sm px-2 py-0.5 rounded-full ${isBuy ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{signal.signal}</span>
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-300 italic">{signal.summary}</p>
            <div className="space-y-3 mt-2">
              <Reason title="Technical Analysis" body={signal.reasoning.technical} color="text-emerald-400" />
              <Reason title="Chart Pattern" body={signal.reasoning.pattern} color="text-cyan-400" />
              <Reason title="Market Sentiment" body={signal.reasoning.sentiment} color="text-violet-400" />
              <Reason title="Execution Plan" body={signal.reasoning.execution} color="text-amber-400" />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const Stat = ({ label, value, color, icon }) => (
  <div className="rounded-lg bg-slate-900/50 border border-slate-800 py-2">
    <div className="text-[10px] text-slate-500 flex items-center justify-center gap-1">{icon}{label}</div>
    <div className={`font-mono-ticker text-sm font-semibold ${color}`}>{value}</div>
  </div>
);

const Reason = ({ title, body, color }) => (
  <div className="rounded-lg bg-slate-900/50 border border-slate-800 p-3">
    <p className={`text-xs font-semibold uppercase tracking-wide ${color} mb-1`}>{title}</p>
    <p className="text-sm text-slate-300 leading-relaxed">{body}</p>
  </div>
);
